import { memo, useContext, useEffect, useMemo } from "react"
import { UserContext } from "../../context/userContext"
import _ from "lodash"
import { useLocation, useNavigate } from "react-router-dom"
import { PanelMenu } from "primereact/panelmenu"
import { useMicroservices } from "../../hooks/useMicroservices"
import useMainMenu from "../../hooks/useMainMenu"
import { LoaderMedium } from "../Loaders"
import {
  buildPanelMenuModel,
  renderMaterialIconMenuItemTemplate,
} from "./components/helpers/menuBuilder"

import baseStyles from "../../styles/SidebarPanelMenuBase.module.css"

const GENERIC_MENU_ITEM_ICON = "analytics"

function SidebarDashboardSupervisor() {
  const userCtx = useContext(UserContext)
  const currentUserRole = Number(userCtx.session.user.staff_role) || null
  const navigate = useNavigate()
  const location = useLocation()

  const { isMicroserviceEnabled } = useMicroservices()
  const {
    menuItems: dashboardMenuItems,
    loading: loadingDashboardMenu,
    resolveMenuPath,
  } = useMainMenu({
    // Pull only the Dashboard parent section from main menu config.
    includeLabels: ["Dashboard"],
  })

  const fallbackMenuItems = useMemo(() => {
    // Safety fallback when Dashboard config is missing or has no usable leaf links.
    const items = [
      {
        label: "Dashboard",
        path: "/",
        role: 4,
        icon: "trending_up",
      },
      {
        label: "Reporting",
        path: "/actions/organization-reports",
        role: 3,
        icon: "analytics",
      },
      {
        label: "Audit Log",
        path: "/organization/audit-log",
        role: 3,
        icon: "fact_check",
      },
    ]

    if (isMicroserviceEnabled(10)) {
      items.push({
        label: "Kiosk Dashboard",
        path: "/kiosk-dashboard",
        role: 6,
        icon: "table_chart",
      })
    }

    return items
  }, [isMicroserviceEnabled])

  const menuItems = useMemo(() => {
    const dashboardChildren = Array.isArray(dashboardMenuItems?.[0]?.items)
      ? dashboardMenuItems[0].items
      : []

    const findFallbackIcon = (itemLabel, itemPath) => {
      const normalizedPath = `${itemPath || ""}`.trim()
      const normalizedLabel = `${itemLabel || ""}`.trim().toLowerCase()

      const fallbackByPath = fallbackMenuItems.find(
        (fallbackItem) => fallbackItem?.path === normalizedPath,
      )
      if (fallbackByPath?.icon) {
        return fallbackByPath.icon
      }

      const fallbackByLabel = fallbackMenuItems.find(
        (fallbackItem) =>
          `${fallbackItem?.label || ""}`.trim().toLowerCase() === normalizedLabel,
      )

      // Fallback order: hardcoded by path, then hardcoded by label, then generic icon.
      return fallbackByLabel?.icon || GENERIC_MENU_ITEM_ICON
    }

    const dashboardLeafItems = dashboardChildren
      .filter((item) => {
        return (
          !!item?.label &&
          typeof item?.path === "string" &&
          item.path.trim().length > 0
        )
      })
      .map((item) => ({
        label: item.label,
        path: resolveMenuPath(item.path),
        icon: item.icon || findFallbackIcon(item.label, resolveMenuPath(item.path)),
      }))

    return dashboardLeafItems.length ? dashboardLeafItems : fallbackMenuItems
  }, [dashboardMenuItems, fallbackMenuItems, resolveMenuPath])

  const supervisorMenuModel = useMemo(() => {
    return buildPanelMenuModel({
      items: menuItems,
      pathname: location.pathname,
      navigate,
      itemTemplate: renderMaterialIconMenuItemTemplate,
      activeClassName: baseStyles.menuLinkActiveItem,
      includeItem: ({ item }) =>
        typeof item?.role !== "number" ||
        item.role >= 4 ||
        currentUserRole < 4,
      onLeafCommand: ({ item }) => {
        const isReportsItem =
          item.path === "/actions/organization-reports" ||
          ["reports", "reporting"].includes(
            `${item.label || ""}`.trim().toLowerCase(),
          )

        window.localStorage.setItem(
          "ACTIONS_MENU",
          isReportsItem ? "reports" : "organization",
        )
      },
    })
  }, [currentUserRole, location.pathname, menuItems, navigate])

  useEffect(() => {
    if (_.isNull(window.localStorage.getItem("ACTIONS_MENU"))) {
      window.localStorage.setItem("ACTIONS_MENU", "organization")
    }
  }, [])

  // Avoid showing fallback links first and then swapping to configured links.
  if (loadingDashboardMenu) {
    return (
      <div className={baseStyles.menuRoot}>
        <LoaderMedium />
      </div>
    )
  }

  if (!supervisorMenuModel.length) return null

  return (
    <div className={baseStyles.menuRoot}>
      <PanelMenu
        key={`dashboard-supervisor-menu-${location.pathname}`}
        model={supervisorMenuModel}
        multiple={false}
      />
    </div>
  )
}

export default memo(SidebarDashboardSupervisor)